import React, { useEffect, useState, useCallback } from 'react';
import { reEnquiryAPI, userAPI, formatApiErrorDetail } from '../api';
import { useAuth } from '../context/AuthContext';
import { formatDateTime } from '../lib/timeFormat';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

export const ReEnquiriesPage = () => {
  const { user } = useAuth();
  const tf = user?.time_format || '12h';
  const [mobile, setMobile] = useState('');
  const [lookup, setLookup] = useState(null);
  const [checking, setChecking] = useState(false);
  const [execs, setExecs] = useState([]);
  const [form, setForm] = useState({ assigned_to: '', service: '', remarks: '', priority: 'medium' });
  const [list, setList] = useState([]);

  const loadList = useCallback(async () => {
    try { const res = await reEnquiryAPI.list(); setList(res.data.re_enquiries || []); } catch (e) { /* ignore */ }
  }, []);
  useEffect(() => { loadList(); userAPI.getAll().then(r => setExecs((r.data || []).filter(u => u.role === 'sales_executive'))).catch(() => {}); }, [loadList]);

  const check = async () => {
    if (!mobile.trim()) return;
    setChecking(true); setLookup(null);
    try { const res = await reEnquiryAPI.existingByMobile(mobile.trim()); setLookup(res.data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail, 'Lookup failed')); }
    finally { setChecking(false); }
  };

  const createRe = async () => {
    if (!lookup?.lead?.id) return;
    try {
      await reEnquiryAPI.create(lookup.lead.id, form);
      toast.success('Re-enquiry created (does not increase Total Leads)');
      setForm({ assigned_to: '', service: '', remarks: '', priority: 'medium' });
      await check(); await loadList();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail, 'Failed to create re-enquiry')); }
  };

  return (
    <div className="space-y-6" data-testid="re-enquiries-page">
      <div>
        <h1 className="text-3xl font-outfit font-semibold">Duplicate / Re-Enquiry (B2C)</h1>
        <p className="text-muted-foreground mt-1">Look up an existing client by mobile and log a re-enquiry linked to the original lead.</p>
      </div>

      <div className="clay-card p-4">
        <label className="block text-sm font-medium mb-2">Client Mobile Number</label>
        <div className="flex gap-2">
          <input value={mobile} onChange={e => setMobile(e.target.value)} data-testid="re-mobile" placeholder="e.g. +9198..."
            className="flex-1 px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900" />
          <button onClick={check} disabled={checking} data-testid="re-check" className="px-5 py-2 bg-primary text-white rounded-lg disabled:opacity-50">{checking ? 'Checking...' : 'Check'}</button>
        </div>

        {lookup && !lookup.exists && (
          <p className="mt-3 text-sm text-green-600" data-testid="re-none">No existing lead for this number — create a normal new lead instead.</p>
        )}
        {lookup && lookup.exists && (
          <div className="mt-4 border border-slate-200 dark:border-slate-700 rounded-lg p-4 space-y-3" data-testid="re-existing">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">{lookup.lead.customer_name} <span className="text-xs uppercase ml-2 px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800">{lookup.client_type}</span></p>
                <p className="text-sm text-muted-foreground">Status: {lookup.lead.status} · Current Exec: {lookup.lead.assigned_to_name || 'Unassigned'} · Re-enquiries so far: {lookup.reenquiry_count}</p>
              </div>
              <Link to={`/leads/${lookup.lead.id}`} className="text-primary text-sm hover:underline">Open lead</Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 pt-2 border-t border-slate-100 dark:border-slate-800">
              <select value={form.assigned_to} onChange={e => setForm({ ...form, assigned_to: e.target.value })} data-testid="re-assign"
                className="px-2 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm">
                <option value="">Reconnect Executive</option>
                {execs.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
              <input value={form.service} onChange={e => setForm({ ...form, service: e.target.value })} placeholder="Service" data-testid="re-service"
                className="px-2 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm" />
              <input value={form.remarks} onChange={e => setForm({ ...form, remarks: e.target.value })} placeholder="Remarks" data-testid="re-remarks"
                className="px-2 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm" />
              <button onClick={createRe} data-testid="re-create" className="px-4 py-2 bg-primary text-white rounded-lg text-sm">Create Re-Enquiry</button>
            </div>
          </div>
        )}
      </div>

      <div className="clay-card overflow-x-auto">
        <h3 className="font-medium px-4 pt-4">All Re-Enquiries ({list.length})</h3>
        <table className="w-full text-sm mt-2">
          <thead className="bg-slate-50 dark:bg-slate-800/50 text-left">
            <tr>{['Client', 'Mobile', 'Service', 'Status', 'Assigned To', 'Created', 'Original'].map(h => <th key={h} className="px-3 py-3 font-medium">{h}</th>)}</tr>
          </thead>
          <tbody>
            {list.length === 0 ? <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">No re-enquiries yet.</td></tr> :
              list.map(r => (
                <tr key={r.id} className="border-t border-slate-100 dark:border-slate-800" data-testid="re-row">
                  <td className="px-3 py-2">{r.customer_name}</td>
                  <td className="px-3 py-2">{r.primary_mobile}</td>
                  <td className="px-3 py-2">{r.service || '\u2014'}</td>
                  <td className="px-3 py-2">{r.status}</td>
                  <td className="px-3 py-2">{r.assigned_to_name || '\u2014'}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{r.created_at ? formatDateTime(r.created_at, tf) : '\u2014'}</td>
                  <td className="px-3 py-2">{r.parent_lead_id ? <Link to={`/leads/${r.parent_lead_id}`} className="text-primary hover:underline">Original</Link> : '\u2014'}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
export default ReEnquiriesPage;
